/**
 * SpatialHash - Efficient spatial partitioning for collision detection
 * O(1) lookups instead of O(n²) comparisons
 */

export class SpatialHash {
  private cellSize: number
  private grid: Map<string, Set<number>>

  constructor(cellSize = 50) {
    this.cellSize = cellSize
    this.grid = new Map()
  }

  /**
   * Get cell key for position
   */
  private getCellKey(x: number, y: number): string {
    const cellX = Math.floor(x / this.cellSize)
    const cellY = Math.floor(y / this.cellSize)
    return `${cellX},${cellY}`
  }

  /**
   * Add node to spatial hash
   */
  addNode(nodeIndex: number, x: number, y: number): void {
    const key = this.getCellKey(x, y)
    if (!this.grid.has(key)) {
      this.grid.set(key, new Set())
    }
    this.grid.get(key)!.add(nodeIndex)
  }

  /**
   * Remove node from spatial hash
   */
  removeNode(nodeIndex: number, x: number, y: number): void {
    const key = this.getCellKey(x, y)
    const cell = this.grid.get(key)
    if (cell) {
      cell.delete(nodeIndex)
      if (cell.size === 0) {
        this.grid.delete(key)
      }
    }
  }

  /**
   * Get nearby nodes within radius
   */
  getNearby(x: number, y: number, radius: number): Set<number> {
    const nearby = new Set<number>()
    const cellRadius = Math.ceil(radius / this.cellSize)
    const centerCellX = Math.floor(x / this.cellSize)
    const centerCellY = Math.floor(y / this.cellSize)

    // Check surrounding cells
    for (let dy = -cellRadius; dy <= cellRadius; dy++) {
      for (let dx = -cellRadius; dx <= cellRadius; dx++) {
        const key = `${centerCellX + dx},${centerCellY + dy}`
        const cell = this.grid.get(key)
        if (cell) {
          cell.forEach((nodeIndex) => nearby.add(nodeIndex))
        }
      }
    }

    return nearby
  }

  /**
   * Clear the spatial hash
   */
  clear(): void {
    this.grid.clear()
  }

  /**
   * Rebuild the entire spatial hash (useful when many nodes move)
   */
  rebuild(nodes: Array<{ x: number; y: number }>): void {
    this.clear()
    nodes.forEach((node, index) => {
      this.addNode(index, node.x, node.y)
    })
  }
}
